// Face-Parsing Vision Module — uses Xenova/face-parsing (SegFormer fine-tuned
// from nvidia/mit-b5 on CelebAMask-HQ) via Transformers.js.
//
// Why this is better than MediaPipe for our use case:
// • 19-class face parsing — separately labels hair, skin, eyes, brows, lips, ears,
//   neck, clothes, glasses, hat. Far more precise than MediaPipe's 6 classes.
// • Trained on a face-specific dataset, so it handles selfies and portraits
//   better than the generic Selfie Multiclass model.
// • Catches the redhead/long-hair edge cases that MediaPipe struggles with.
//
// Cost: ~30MB model download on first use (cached forever by the browser).
// Inference: ~600-1200ms on a phone, ~200-400ms on a laptop. Acceptable for a
// one-shot analysis (we're not running per-frame video).

import { pipeline, env } from "@huggingface/transformers";

// Allow remote model loading from Hugging Face CDN.
env.allowLocalModels = false;
env.useBrowserCache = true;

let _segmenter = null;
let _loadingPromise = null;

// Label → category mapping for jonathandinu/face-parsing (CelebAMask-HQ schema).
// We care primarily about hair (paint zone) and face features (exclusion zone).
const HAIR_LABELS = new Set(["hair"]);
const FACE_LABELS = new Set([
  "skin",
  "nose",
  "eye_g",       // eyeglasses
  "l_eye", "r_eye",
  "l_brow", "r_brow",
  "l_ear", "r_ear",
  "mouth", "u_lip", "l_lip",
  "neck", "neck_l", // necklace
  "cloth", "hat", "ear_r",
]);

export async function loadFaceParser() {
  if (_segmenter) return _segmenter;
  if (_loadingPromise) return _loadingPromise;
  _loadingPromise = (async () => {
    _segmenter = await pipeline("image-segmentation", "Xenova/face-parsing", {
      // Use the smaller quantized variant if available — significantly smaller download.
      quantized: true,
    });
    return _segmenter;
  })();
  return _loadingPromise;
}

// Run face parsing on the given <img> element.
// Returns the same shape our painter expects:
//   { hairMask: Uint8Array (W*H), faceMask: Uint8Array (W*H), width, height, faceBox }
export async function parseFace(imgElement) {
  const segmenter = await loadFaceParser();

  // Downscale to ≤512px on the longer side — face-parsing SegFormer outputs at this scale.
  const SRC_W = imgElement.naturalWidth;
  const SRC_H = imgElement.naturalHeight;
  const maxDim = 512;
  const scale = Math.min(1, maxDim / Math.max(SRC_W, SRC_H));
  const W = Math.round(SRC_W * scale);
  const H = Math.round(SRC_H * scale);

  // Render to a canvas and produce a Blob URL — Transformers.js prefers URLs/Image inputs.
  const work = document.createElement("canvas");
  work.width = W; work.height = H;
  work.getContext("2d").drawImage(imgElement, 0, 0, W, H);
  const blob = await new Promise(resolve => work.toBlob(resolve, "image/png"));
  const blobUrl = URL.createObjectURL(blob);

  let output;
  try {
    output = await segmenter(blobUrl);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }

  // Build the hair + face masks at the segmenter's native resolution.
  // Transformers.js returns each segment as { label, mask: RawImage } where mask is greyscale W×H.
  let nativeW = W, nativeH = H;
  const hairMask = new Uint8Array(W * H);
  const faceMask = new Uint8Array(W * H);

  for (const seg of output) {
    const label = seg.label.toLowerCase();
    const mask = seg.mask; // RawImage with .data (Uint8Array), .width, .height
    nativeW = mask.width;
    nativeH = mask.height;
    const isHair = HAIR_LABELS.has(label);
    const isFace = FACE_LABELS.has(label);
    if (!isHair && !isFace) continue;
    const target = isHair ? hairMask : faceMask;
    // Resample mask to our W×H grid (nearest neighbour — fast and adequate for a binary mask).
    if (nativeW === W && nativeH === H) {
      for (let i = 0; i < mask.data.length; i++) {
        if (mask.data[i] > 127) target[i] = 1;
      }
    } else {
      for (let py = 0; py < H; py++) {
        for (let px = 0; px < W; px++) {
          const sx = Math.floor((px / W) * nativeW);
          const sy = Math.floor((py / H) * nativeH);
          if (mask.data[sy * nativeW + sx] > 127) target[py * W + px] = 1;
        }
      }
    }
  }

  // Clean hair mask: 2px erosion against face mask (kills any soft halo near the face).
  const cleanedHair = new Uint8Array(W * H);
  for (let py = 2; py < H - 2; py++) {
    for (let px = 2; px < W - 2; px++) {
      const idx = py * W + px;
      if (hairMask[idx] !== 1) continue;
      // 4-neighbour hair check.
      if (!(hairMask[idx - 1] && hairMask[idx + 1] && hairMask[idx - W] && hairMask[idx + W])) continue;
      // Reject if any pixel within 2px is in faceMask.
      let nearFace = false;
      for (let dy = -2; dy <= 2 && !nearFace; dy++) {
        for (let dx = -2; dx <= 2 && !nearFace; dx++) {
          if (faceMask[(py + dy) * W + (px + dx)]) nearFace = true;
        }
      }
      if (nearFace) continue;
      cleanedHair[idx] = 1;
    }
  }

  // Bounding box of hair (used to crop the patch we send to Gemini).
  let minX = W, minY = H, maxX = 0, maxY = 0, count = 0;
  for (let py = 0; py < H; py++) {
    for (let px = 0; px < W; px++) {
      if (cleanedHair[py * W + px]) {
        count++;
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
    }
  }
  const hairBox = count > 0 ? { x: minX, y: minY, w: maxX - minX, h: maxY - minY } : null;

  return { hairMask: cleanedHair, faceMask, width: W, height: H, hairBox };
}

// Crop a tight rectangle around hair pixels and return base64 JPEG.
export function cropHairPatchFromFaceParse(imgElement, parseResult, quality = 0.75) {
  if (!parseResult?.hairBox) return null;
  const { hairBox, width: W, height: H } = parseResult;
  const SRC_W = imgElement.naturalWidth;
  const SRC_H = imgElement.naturalHeight;
  const sx = (hairBox.x / W) * SRC_W;
  const sy = (hairBox.y / H) * SRC_H;
  const sw = (hairBox.w / W) * SRC_W;
  const sh = (hairBox.h / H) * SRC_H;
  const outMax = 640;
  const outScale = Math.min(1, outMax / Math.max(sw, sh));
  const outW = Math.round(sw * outScale);
  const outH = Math.round(sh * outScale);
  if (outW < 16 || outH < 16) return null;
  const out = document.createElement("canvas");
  out.width = outW; out.height = outH;
  out.getContext("2d").drawImage(imgElement, sx, sy, sw, sh, 0, 0, outW, outH);
  return out.toDataURL("image/jpeg", quality);
}
